import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Typography, Space, Rate } from 'antd';
import { useToast } from '../../contexts/ToastContext';
import { StarOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface IncomingReferenceRequest {
  id: number;
  content: string;
  state: boolean;
  createdAt: string;
  mentorName?: string;
  menteeName?: string;
  mentorId?: number;
  menteeId?: number;
  mentorBookingId?: number;
  bootcampApplicationId?: number;
}

interface ReferenceApprovalModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  referenceRequest: IncomingReferenceRequest | null;
  userType: 'mentee' | 'mentor';
}

const ReferenceApprovalModal: React.FC<ReferenceApprovalModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  referenceRequest,
  userType
}) => {
  const toast = useToast();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);

  const handleSubmit = async (values: { content: string; title?: string }) => {
    if (!referenceRequest) return;

    if (rating === 0) {
      toast.error('Lütfen 1-5 arası puan verin!');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        content: values.content,
        rating: rating,
        title: values.title || '',
        referenceRequestId: referenceRequest.id
      };

      let response;
      if (userType === 'mentee') {
        response = await apiService.createMenteeReference(requestData);
      } else {
        response = await apiService.createMentorReference(requestData);
      }

      if (response.success) {
        toast.success('Referans başarıyla oluşturuldu!');
        form.resetFields();
        setRating(0);
        onSuccess();
        onCancel();
      } else {
        toast.error(response.message || 'Referans oluşturulamadı.');
      }
    } catch (error) {
      console.error('Referans oluşturulurken hata:', error);
      toast.error('Referans oluşturulurken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setRating(0);
    }
  }, [visible, form]);

  const getRequesterName = () => {
    if (!referenceRequest) return '';
    return userType === 'mentee' ? referenceRequest.mentorName : referenceRequest.menteeName;
  };

  return (
    <Modal
      title={<Title level={4} style={{ margin: 0 }}>Referans Onayla</Title>}
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      width={700}
    >
      <div style={{ padding: '20px 0' }}>
        {referenceRequest && (
          <div style={{ marginBottom: '20px', padding: '15px', background: '#f0f2f5', borderRadius: '8px' }}>
            <Text strong>Referans Talebi:</Text>
            <div style={{ marginTop: '10px' }}>
              <Text><strong>Gönderen:</strong> {getRequesterName()}</Text>
            </div>
            <div style={{ marginTop: '5px' }}>
              <Text><strong>Talep:</strong> {referenceRequest.content}</Text>
            </div>
            <div style={{ marginTop: '5px' }}>
              <Text><strong>Tarih:</strong> {new Date(referenceRequest.createdAt).toLocaleDateString('tr-TR')}</Text>
            </div>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ content: '', title: '' }}
        >
          <Form.Item
            name="title"
            label="Referans Başlığı (Opsiyonel)"
          >
            <Input placeholder="Referans için bir başlık yazın..." />
          </Form.Item>

          <Form.Item
            label="Puanlama"
            required
          >
            <Rate
              value={rating}
              onChange={setRating}
              character={<StarOutlined />}
              style={{ fontSize: '24px' }}
            />
            <Text type="secondary" style={{ marginLeft: '10px' }}>
              {rating > 0 ? `${rating} yıldız` : '1-5 arası puan verin'}
            </Text>
          </Form.Item>

          <Form.Item
            name="content"
            label="Referans İçeriği"
            rules={[{ required: true, message: 'Lütfen referans içeriğini girin!' }]}
          >
            <TextArea 
              rows={6} 
              placeholder="Referansınızı detaylı bir şekilde yazın..." 
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={onCancel}>
                İptal
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                disabled={rating === 0}
                icon={<StarOutlined />}
              >
                Referansı Onayla
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default ReferenceApprovalModal;
